<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NativeWhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import channelTemplatePresets from 'custom/api/channelTemplatePresets';
import {
  TEMPLATE_PRESET_PURPOSES,
  useTemplateSendPreset,
} from 'custom/composables/useTemplateSendPreset';
import TemplateCatalogPicker from './TemplateCatalogPicker.vue';
import TemplateSendFields from './TemplateSendFields.vue';
import {
  previewVariables,
  templateWithHeaderPreview,
  templateWithMediaPreview,
} from 'custom/helpers/channelTemplatePresets';

const props = defineProps({
  show: { type: Boolean, default: false },
  inboxId: { type: Number, default: undefined },
  conversationId: { type: Number, default: undefined },
});

const emit = defineEmits(['onSend', 'cancel', 'update:show', 'close']);
const { t } = useI18n();
const selectedTemplate = ref(null);
const parser = ref(null);
const featureEnabled = ref(null);
const templates = shallowRef([]);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const {
  currentMediaUrl,
  initializePreset,
  loadingPreset,
  mediaSource,
  processedParams,
  resetPreset,
  statusLabel,
  statusMessage,
  statusTone,
  uploadedMediaName,
  uploadingMedia,
  uploadMedia: prepareMedia,
} = useTemplateSendPreset({
  parser,
  template: selectedTemplate,
  inboxId: computed(() => props.inboxId),
  purpose: TEMPLATE_PRESET_PURPOSES.CONVERSATION_REPLY,
  conversationId: computed(() => props.conversationId),
});

const previewTemplate = computed(() => {
  if (!selectedTemplate.value) return null;
  const withMedia = templateWithMediaPreview(
    selectedTemplate.value,
    currentMediaUrl.value
  );
  return templateWithHeaderPreview(withMedia, processedParams.value.header);
});
const variables = computed(() => previewVariables(processedParams.value));
const headerTextFields = computed(() =>
  Object.keys(processedParams.value.header || {}).filter(
    key => !['media_url', 'media_type', 'media_name'].includes(key)
  )
);
const hasEmptyHeaderText = computed(() =>
  headerTextFields.value.some(key => !processedParams.value.header[key])
);
const showValidationError = computed(
  () => !!(parser.value?.v$?.$dirty && parser.value?.v$?.$invalid)
);

const reset = () => {
  selectedTemplate.value = null;
  resetPreset();
};

const close = () => {
  localShow.value = false;
  reset();
  emit('cancel');
  emit('close');
};

const fallbackToNative = () => {
  featureEnabled.value = false;
  templates.value = [];
};

const loadCatalog = async () => {
  const inboxId = props.inboxId;
  const conversationId = props.conversationId;
  featureEnabled.value = null;
  templates.value = [];
  try {
    const { data } = await channelTemplatePresets.catalog(inboxId, {
      purpose: TEMPLATE_PRESET_PURPOSES.CONVERSATION_REPLY,
      conversationId,
    });
    if (
      !props.show ||
      inboxId !== props.inboxId ||
      conversationId !== props.conversationId
    ) {
      return;
    }
    featureEnabled.value = data.enabled === true;
    templates.value = data.templates || [];
  } catch (error) {
    if (
      props.show &&
      inboxId === props.inboxId &&
      conversationId === props.conversationId
    ) {
      fallbackToNative();
    }
  }
};

const forwardNativeVisibility = value => emit('update:show', value);
const forwardNativeSend = payload => emit('onSend', payload);
const forwardNativeCancel = () => emit('cancel');

const pickTemplate = async template => {
  selectedTemplate.value = template;
  await initializePreset();
};

const uploadMedia = async file => {
  try {
    await prepareMedia(file);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CHANNEL_TEMPLATE_PRESETS.UPLOAD_ERROR')
    );
  }
};

const send = payload => {
  emit('onSend', payload);
  localShow.value = false;
  reset();
};

watch(
  () => props.show,
  value => {
    if (value) {
      loadCatalog();
      return;
    }
    featureEnabled.value = null;
    templates.value = [];
    reset();
  },
  { immediate: true }
);
</script>

<template>
  <NativeWhatsappTemplates
    v-if="featureEnabled === false"
    :show="show"
    :inbox-id="inboxId"
    @update:show="forwardNativeVisibility"
    @on-send="forwardNativeSend"
    @cancel="forwardNativeCancel"
  />

  <woot-modal
    v-else-if="featureEnabled === true"
    v-model:show="localShow"
    :on-close="close"
    size="!w-11/12 !max-w-6xl"
  >
    <div class="flex max-h-[92vh] w-full flex-col">
      <header class="shrink-0 border-b border-n-weak px-6 py-5 pr-12">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('CHANNEL_TEMPLATE_PRESETS.SEND_TITLE') }}
          </h2>
          <span
            v-if="selectedTemplate"
            class="rounded-md px-2 py-0.5 text-xs font-medium"
            :class="statusTone"
          >
            {{
              loadingPreset
                ? t('CHANNEL_TEMPLATE_PRESETS.LOADING_PRESET')
                : statusLabel
            }}
          </span>
        </div>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            selectedTemplate
              ? `${selectedTemplate.name} · ${selectedTemplate.language}`
              : t('CHANNEL_TEMPLATE_PRESETS.SEND_DESCRIPTION')
          }}
        </p>
        <p
          v-if="selectedTemplate && !loadingPreset"
          class="mt-1 text-xs text-n-slate-11"
        >
          {{ statusMessage }}
        </p>
      </header>

      <div class="min-h-0 flex-1 overflow-y-auto p-6">
        <TemplateCatalogPicker
          v-if="!selectedTemplate"
          :templates="templates"
          @select="pickTemplate"
        />

        <div
          v-else
          class="grid items-start gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(20rem,5fr)]"
        >
          <div class="min-w-0">
            <TemplateSendFields
              :template="selectedTemplate"
              :processed-params="processedParams"
              :media-source="mediaSource"
              :uploaded-media-name="uploadedMediaName"
              :uploading-media="uploadingMedia"
              :has-error="showValidationError"
              @upload-media="uploadMedia"
            />

            <WhatsAppTemplateParser
              ref="parser"
              headless
              :template="selectedTemplate"
              @send-message="send"
              @reset-template="reset"
            >
              <template #actions="{ sendMessage, resetTemplate, disabled }">
                <div
                  class="mt-6 flex flex-col-reverse gap-3 border-t border-n-weak pt-5 sm:flex-row sm:justify-end"
                >
                  <NextButton
                    faded
                    slate
                    :label="t('CHANNEL_TEMPLATE_PRESETS.BACK')"
                    @click="resetTemplate"
                  />
                  <NextButton
                    :label="t('CHANNEL_TEMPLATE_PRESETS.SEND')"
                    :disabled="
                      disabled ||
                      hasEmptyHeaderText ||
                      loadingPreset ||
                      uploadingMedia
                    "
                    @click="sendMessage"
                  />
                </div>
              </template>
            </WhatsAppTemplateParser>
          </div>

          <aside
            class="min-w-0 rounded-xl border border-n-weak bg-n-alpha-1 p-4 lg:sticky lg:top-0"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('CHANNEL_TEMPLATE_PRESETS.PREVIEW') }}
            </h3>
            <div class="mt-4 flex justify-center">
              <TemplatePreview
                :template="previewTemplate"
                :variables="variables"
                :platform="PLATFORMS.WHATSAPP"
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
